import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE_URL } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Send, Loader2, Paperclip, X, FileText } from "lucide-react";
import RecipientChipsInput, { isValidEmailAddr } from "@/components/RecipientChipsInput";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const ALLOWED_EXTS = [".jpg", ".jpeg", ".png", ".pdf"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

function formatSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * PRO-specific compose dialog. Sends via /api/pro/messages/send.
 *
 * Supports multi-recipient (chips) and JPG/PNG/PDF attachments up to 5 MB
 * each. When files are attached, the request switches to multipart/form-data.
 *
 * Props:
 *   open, onOpenChange: dialog controls
 *   aliasAddress: the PRO alias (displayed as From:)
 *   prefill: optional { to, subject, body, replyToMessageId }
 *   onSent: callback after a successful send
 */
export default function ProComposeEmail({
  open, onOpenChange, aliasAddress, prefill, onSent,
}) {
  const [to, setTo] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [replyToId, setReplyToId] = useState(null);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const fileInputRef = useRef(null);

  // Apply prefill every time the dialog opens
  useEffect(() => {
    if (open) {
      const rawTo = prefill?.to;
      const arr = Array.isArray(rawTo)
        ? rawTo
        : (rawTo || "").split(/[,;]/).map((s) => s.trim()).filter(Boolean);
      setTo(arr);
      setSubject(prefill?.subject || "");
      setBody(prefill?.body || "");
      setReplyToId(prefill?.replyToMessageId || null);
      setFiles([]);
      setSendError("");
    }
  }, [open, prefill]);

  const validRecipients = to.filter((r) => isValidEmailAddr(r));
  const hasInvalid = to.some((r) => !isValidEmailAddr(r));
  const missingTo = to.length === 0;
  const missingSubject = !subject.trim();
  const missingBody = !body.trim();

  let disableReason = "";
  if (missingTo) disableReason = "At least one recipient is required";
  else if (hasInvalid) disableReason = "One or more recipients are invalid";
  else if (missingSubject) disableReason = "Subject is required";
  else if (missingBody) disableReason = "Message body is required";

  const canSend = !sending && !disableReason;

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = [];
    for (const f of selected) {
      const ext = "." + f.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXTS.includes(ext) || !ALLOWED_TYPES.includes(f.type)) {
        toast.error(`File type not allowed: ${f.name}. Only JPG, PNG, PDF.`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`File too large: ${f.name} (max 5 MB)`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setSendError("");
    const recipientsCsv = validRecipients.join(",");
    try {
      let data;
      if (files.length > 0) {
        const fd = new FormData();
        fd.append("to", recipientsCsv);
        fd.append("subject", subject.trim());
        fd.append("body_text", body.trim());
        if (replyToId) fd.append("reply_to_message_id", replyToId);
        files.forEach((f) => fd.append("files", f));
        const res = await fetch(`${API_BASE_URL}/api/pro/messages/send`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw { response: { data: err } };
        }
        data = await res.json();
      } else {
        const payload = {
          to: recipientsCsv,
          subject: subject.trim(),
          body_text: body.trim(),
        };
        if (replyToId) payload.reply_to_message_id = replyToId;
        const r = await api.post("/pro/messages/send", payload);
        data = r.data;
      }
      toast.success(validRecipients.length > 1 ? `Sent to ${validRecipients.length} recipients` : "Email sent");
      setTo([]); setSubject(""); setBody(""); setReplyToId(null); setFiles([]);
      onOpenChange(false);
      if (onSent) onSent(data);
    } catch (err) {
      const msg = getErrorMessage(err) || "Could not send email. Please try again.";
      setSendError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const totalAttachSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="pro-compose-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {replyToId ? "Reply" : (prefill?.subject?.startsWith("Fwd:") ? "Forward" : "New message")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-[#555555]">From</Label>
            <div
              className="h-9 rounded-md border border-[#E5E5E5] bg-[#F9F9F9] px-3 flex items-center text-xs font-mono text-[#555555]"
              data-testid="pro-compose-from"
            >
              {aliasAddress || "—"}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-[#555555]">To</Label>
            <RecipientChipsInput
              value={to}
              onChange={setTo}
              placeholder="recipient@example.com (comma-separated)"
              testId="pro-compose-to"
            />
            {to.length > 1 && (
              <p className="text-[11px] text-zinc-500" data-testid="pro-compose-to-count">
                {to.length} recipients
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-[#555555]">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="h-9 text-sm"
              data-testid="pro-compose-subject"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-[#555555]">Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message…"
              className="min-h-[180px] text-sm"
              data-testid="pro-compose-body"
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Button
                type="button" variant="outline" size="sm" className="h-8 text-xs text-[#555555]"
                onClick={() => fileInputRef.current?.click()}
                data-testid="pro-compose-attach-button"
              >
                <Paperclip size={14} className="mr-1.5" /> Attach files
              </Button>
              <span className="text-[10px] text-zinc-500">JPG, PNG, PDF — max 5 MB each</span>
            </div>
            <input
              ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" multiple
              className="hidden" onChange={handleFileSelect} data-testid="pro-compose-file-input"
            />
            {files.length > 0 && (
              <div className="space-y-1" data-testid="pro-compose-attached-files">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 bg-[#F5F5F5] rounded-md px-2.5 py-1.5 border border-[#E5E5E5]" data-testid={`pro-compose-file-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-zinc-500 shrink-0" />
                      <span className="text-xs text-[#555555] truncate">{f.name}</span>
                      <span className="text-[10px] text-zinc-500 font-mono shrink-0">{formatSize(f.size)}</span>
                    </div>
                    <button onClick={() => removeFile(i)} className="text-zinc-400 hover:text-red-500 shrink-0" data-testid={`pro-compose-file-remove-${i}`}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
                <p className="text-[10px] text-zinc-500 font-mono">{files.length} file{files.length > 1 ? "s" : ""} — {formatSize(totalAttachSize)} total</p>
              </div>
            )}
          </div>

          {sendError && (
            <div
              className="px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-600"
              data-testid="pro-compose-error"
            >
              {sendError}
            </div>
          )}

          <p className="text-[11px] text-zinc-500">
            Replies will come back to your PRO inbox at <span className="font-mono">{aliasAddress}</span>.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="pro-compose-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="bg-[var(--color-l-accent,#CC0000)] hover:bg-[var(--color-l-accent-h,#A80000)] text-white"
            data-testid="pro-compose-send"
            title={disableReason || ""}
          >
            {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
            {sending ? "Sending…" : (files.length > 0 ? `Send with ${files.length} file${files.length > 1 ? "s" : ""}` : "Send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
